package com.iregained.eventapp.components.sections.event_details.sections

import androidx.compose.runtime.Composable
import com.iregained.eventapp.components.widgets.SharedHeroActionLink
import com.iregained.eventapp.components.widgets.SharedHeroWidget
import com.iregained.eventapp.components.widgets.VideoFormat
import com.iregained.eventapp.components.widgets.styles.HeaderSecondaryActionStyle
import com.iregained.eventapp.components.widgets.styles.PrimaryButtonStyle
import com.iregained.eventapp.theme.toSitePalette
import com.iregained.eventapp.utils.Res
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.s

val HeroFadeInKeyframes = Keyframes {
    0.percent { Modifier.opacity(0) }
    100.percent { Modifier.opacity(1) }
}

@Composable
fun EventHeroSection(data: ProductHeroSectionData = defaultProductHeroSectionData) {
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-header-section"
    val palette = ColorMode.current.toSitePalette()

    if (data.headline.isNotBlank()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().background(Colors.White).color(palette.custom.darkText)
                .animation(
                    HeroFadeInKeyframes.toAnimation(
                        duration = 1.5.s, timingFunction = AnimationTimingFunction.EaseInOut
                    )
                )
        ) {
            SharedHeroWidget(
                overline = data.overline,
                headline = data.headline,
                description = data.description,
                imagePath = data.imagePath,
                heroVideoFormat = VideoFormat.SQUARE_1_1,
                actions = {
                    if (data.primaryCtaText.isNotBlank() && data.primaryCtaPath.isNotBlank()) {
                        SharedHeroActionLink(
                            text = data.primaryCtaText,
                            path = data.primaryCtaPath,
                            styleModifier = PrimaryButtonStyle.toModifier()
                        )
                    }
                    if (data.secondaryCtaText.isNotBlank() && data.secondaryCtaPath.isNotBlank()) {
                        SharedHeroActionLink(
                            text = data.secondaryCtaText,
                            path = data.secondaryCtaPath,
                            styleModifier = HeaderSecondaryActionStyle.toModifier()
                        )
                    }
                },
            )
        }
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field imagePath:
 * - Aspect Ratio: 4:3 (Landscape) or 1:1.
 * - Recommended Dimensions: 800x600px.
 * - Max File Size: 300KB.
 * - Behavior: Fills half screen on Desktop, scales gracefully on mobile (ObjectFit.Cover).
 */
data class ProductHeroSectionData(
    val sectionId: String? = null,
    val overline: String,
    val headline: String,
    val description: String,
    val primaryCtaText: String,
    val primaryCtaPath: String,
    val secondaryCtaText: String,
    val secondaryCtaPath: String,
    val imagePath: String
)

val defaultProductHeroSectionData = ProductHeroSectionData(
    sectionId = "product-header-section",
    overline = "Transformational neuro-rehabilitation for the hand!",
    headline = "The MyHand<sup>®</sup> System",
    description = "The MyHand<sup>®</sup> System is a targeted rehabilitation system for the hand. It is built on a clinically-proven, patented, AI-enabled, digital neurotherapeutics (DTx) platform that provides neuromechanical restitution to people living with hand function impairment as a result of a neurological or musculoskeletal condition.",
    primaryCtaText = "Join Patient Waitlist",
    primaryCtaPath = "?type=patient#event-registration-section",
    secondaryCtaText = "Host a Showcase",
    secondaryCtaPath = "?type=clinic#event-registration-section",
    imagePath = Res.Images.LANDING.HEADER_IMG
)
